// src/components/Timer/SettingsModal.js

import React, { useState, useEffect } from 'react';
import './SettingsModal.css';

function SettingsModal({ settings, setSettings, onClose }) {
  const predefinedCompletionSounds = [
    { label: "Completed", url: "https://www.youtube.com/watch?v=RgXknSgkivc" },
    { label: "GTA - Misson Passed", url: "https://www.youtube.com/watch?v=7lsdJDiJ0QE" }
  ];

  const [localSettings, setLocalSettings] = useState({
    pomodoroMinutes:     Math.floor(settings.pomodoroDuration / 60),
    pomodoroSeconds:     settings.pomodoroDuration % 60,
    shortBreakMinutes:   Math.floor(settings.shortBreakDuration / 60),
    shortBreakSeconds:   settings.shortBreakDuration % 60,
    longBreakMinutes:    Math.floor(settings.longBreakDuration / 60),
    longBreakSeconds:    settings.longBreakDuration % 60,
    autoStart:           settings.autoStart,
    notificationEnabled: settings.notificationEnabled,
    soundEnabled:        settings.soundEnabled,
    notificationSound:   settings.notificationSound || '',
  });

  const [completionSoundChoice, setCompletionSoundChoice] = useState("");
  const [customCompletionUrl,     setCustomCompletionUrl]     = useState("");

  useEffect(() => {
    // seed localSettings & choice state
    setLocalSettings({
      pomodoroMinutes:     Math.floor(settings.pomodoroDuration / 60),
      pomodoroSeconds:     settings.pomodoroDuration % 60,
      shortBreakMinutes:   Math.floor(settings.shortBreakDuration / 60),
      shortBreakSeconds:   settings.shortBreakDuration % 60,
      longBreakMinutes:    Math.floor(settings.longBreakDuration / 60),
      longBreakSeconds:    settings.longBreakDuration % 60,
      autoStart:           settings.autoStart,
      notificationEnabled: settings.notificationEnabled,
      soundEnabled:        settings.soundEnabled,
      notificationSound:   settings.notificationSound || '',
    });

    const current = settings.notificationSound || "";
    const isPre    = predefinedCompletionSounds.some(s => s.url === current);
    if (isPre) {
      setCompletionSoundChoice(current);
      setCustomCompletionUrl("");
    } else if (current) {
      setCompletionSoundChoice("custom");
      setCustomCompletionUrl(current);
    } else {
      setCompletionSoundChoice(predefinedCompletionSounds[0].url);
      setCustomCompletionUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setLocalSettings(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toTotalSeconds = (min, sec) => Number(min) * 60 + Number(sec);

  const handleSave = () => {
    const newPom   = toTotalSeconds(localSettings.pomodoroMinutes,   localSettings.pomodoroSeconds);
    const newShort = toTotalSeconds(localSettings.shortBreakMinutes, localSettings.shortBreakSeconds);
    const newLong  = toTotalSeconds(localSettings.longBreakMinutes,  localSettings.longBreakSeconds);

    setSettings(prev => ({
      ...prev,
      pomodoroDuration:     newPom,
      shortBreakDuration:   newShort,
      longBreakDuration:    newLong,
      autoStart:            localSettings.autoStart,
      notificationEnabled:  localSettings.notificationEnabled,
      soundEnabled:         localSettings.soundEnabled,
      notificationSound:    localSettings.notificationSound,
    }));
    onClose();
  };

  const handleCancel = () => onClose();

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="close-button" onClick={handleCancel}>&times;</button>
        </div>

        {/* Pomodoro Duration */}
        <div className="form-row">
          <label>Pomodoro Duration:</label>
          <div className="input-row">
            <input
              type="number"
              name="pomodoroMinutes"
              min="0"
              value={localSettings.pomodoroMinutes}
              onChange={handleChange}
              placeholder="Minutes"
              autoFocus
              onFocus={e => e.target.select()}
            />
            <input
              type="number"
              name="pomodoroSeconds"
              min="0"
              max="59"
              value={localSettings.pomodoroSeconds}
              onChange={handleChange}
              placeholder="Seconds"
              onFocus={e => e.target.select()}
            />
          </div>
        </div>

        {/* Short Break */}
        <div className="form-row">
          <label>Short Break Duration:</label>
          <div className="input-row">
            <input
              type="number"
              name="shortBreakMinutes"
              min="0"
              value={localSettings.shortBreakMinutes}
              onChange={handleChange}
              placeholder="Minutes"
              onFocus={e => e.target.select()}
            />
            <input
              type="number"
              name="shortBreakSeconds"
              min="0"
              max="59"
              value={localSettings.shortBreakSeconds}
              onChange={handleChange}
              placeholder="Seconds"
              onFocus={e => e.target.select()}
            />
          </div>
        </div>

        {/* Long Break */}
        <div className="form-row">
          <label>Long Break Duration:</label>
          <div className="input-row">
            <input
              type="number"
              name="longBreakMinutes"
              min="0"
              value={localSettings.longBreakMinutes}
              onChange={handleChange}
              placeholder="Minutes"
              onFocus={e => e.target.select()}
            />
            <input
              type="number"
              name="longBreakSeconds"
              min="0"
              max="59"
              value={localSettings.longBreakSeconds}
              onChange={handleChange}
              placeholder="Seconds"
              onFocus={e => e.target.select()}
            />
          </div>
        </div>

        {/* Auto-Start */}
        <div className="form-row">
          <label>
            <input
              type="checkbox"
              name="autoStart"
              checked={localSettings.autoStart}
              onChange={handleChange}
            />
            Auto-Start Next Session
          </label>
        </div>

        {/* Notifications */}
        <div className="form-row">
          <label>
            <input
              type="checkbox"
              name="notificationEnabled"
              checked={localSettings.notificationEnabled}
              onChange={handleChange}
            />
            Enable Notifications
          </label>
        </div>

        {/* Sound Enabled */}
        <div className="form-row">
          <label>
            <input
              type="checkbox"
              name="soundEnabled"
              checked={localSettings.soundEnabled}
              onChange={handleChange}
            />
            Enable Sound
          </label>
        </div>

        {/* Completion Sound */}
        <div className="form-row">
          <label>Completion Sound:</label>
          <select
            value={completionSoundChoice}
            onChange={e => {
              const val = e.target.value;
              setCompletionSoundChoice(val);
              if (val !== 'custom') {
                setLocalSettings(prev => ({
                  ...prev,
                  notificationSound: val,
                }));
              }
            }}
          >
            {predefinedCompletionSounds.map(sound => (
              <option key={sound.url} value={sound.url}>
                {sound.label}
              </option>
            ))}
            <option value="custom">Custom...</option>
          </select>
        </div>

        {/* Custom URL */}
        {completionSoundChoice === 'custom' && (
          <div className="form-row">
            <input
              type="text"
              value={customCompletionUrl}
              onChange={e => {
                const val = e.target.value;
                setCustomCompletionUrl(val);
                setLocalSettings(prev => ({
                  ...prev,
                  notificationSound: val,
                }));
              }}
              placeholder="Enter YouTube URL or direct audio URL"
            />
          </div>
        )}

        {/* Save / Cancel */}
        <div className="modal-actions">
          <button onClick={handleSave}>Save</button>
          <button onClick={handleCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
